import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Stack,
} from "@mui/material";
import type { LicenseCheck } from "../../models/License";

type LicenseExceededDialogProps = {
    open: boolean;
    onClose: () => void;
    licenseCheck: LicenseCheck | null;
    onUpgrade?: () => void;
    isTenantAdmin?: boolean;
};

const getResourceLabel = (resource?: string) => {
    switch ((resource ?? "").toLowerCase()) {
        case "client":
            return "client";
        case "portfolio":
            return "portfolio";
        case "artwork":
            return "artwork";
        default:
            return "item";
    }
};

const LicenseExceededDialog: React.FC<LicenseExceededDialogProps> = ({
    open,
    onClose,
    licenseCheck,
    onUpgrade,
    isTenantAdmin = false,
}) => {
    const resourceLabel = getResourceLabel(licenseCheck?.resource);

    const title = licenseCheck?.allowed
        ? "License check"
        : `Unable to add ${resourceLabel}`;

    const message =
        licenseCheck?.message ||
        "Your current license does not allow this action.";

    const showCounts =
        typeof licenseCheck?.current === "number" &&
        typeof licenseCheck?.max === "number" &&
        licenseCheck.max > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Alert severity="warning">
                        {message}
                    </Alert>

                    {showCounts && (
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Current usage
                            </Typography>
                            <Typography variant="body1">
                                {licenseCheck?.current} / {licenseCheck?.max}
                            </Typography>
                        </Box>
                    )}

                    {!!licenseCheck?.limitName && (
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Limit type
                            </Typography>
                            <Typography variant="body1">
                                {licenseCheck.limitName}
                            </Typography>
                        </Box>
                    )}

                    {isTenantAdmin ? (
                        <Typography variant="body2" color="text.secondary">
                            Upgrade your plan to increase available limits.
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Please contact your license administrator to request an upgrade.
                        </Typography>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>

                {isTenantAdmin && licenseCheck?.upgradeAvailable && (
                    <Button
                        variant="contained"
                        onClick={onUpgrade}
                    >
                        Upgrade
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default LicenseExceededDialog;